import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import WfTasksPagination from '@/pages/ipe/workbench/components/WfTasksPagination';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import {
  LISTING_EXPORT_DEFAULT_PAGE_SIZE,
  LISTING_EXPORT_GROUP_UNGROUPED,
  LISTING_EXPORT_PAGE_SIZE_OPTIONS,
  loadTableListingTemplateGroupNames,
  loadTableListingTemplatesCursorPage,
  resolveTableListingTemplatesAccess,
} from '@/pages/ipe/workbench/utils/loadTableListingTemplatesScoped';
import '@/styles/ipe/workbench/modals/ListingTemplatePickerModal.css';
import '@/styles/ipe/workbench/IpeWorkflowTasksPage.css';

const PLATFORM_ICON_MAP = {
  TEMU: '/TEMU.png',
  'TEMU半托': '/TEMU.png',
  Shein: '/SHEIN.png',
  SHEIN: '/SHEIN.png',
  'SHEIN半托': '/SHEIN.png',
  TikTok: '/TikTok.png',
  TIKTOK: '/TikTok.png',
  '店小秘': '/DXM.png',
};

function buildPageSizeSelectOptions(t) {
  return LISTING_EXPORT_PAGE_SIZE_OPTIONS.map((size) => ({
    value: size,
    label: t('listingTemplatePicker.pageSizeOption', { size }),
  }));
}

function resolvePlatformIcon(platform) {
  if (!platform) return null;
  const raw = String(platform);
  const key = raw.toUpperCase();
  return PLATFORM_ICON_MAP[raw]
    || PLATFORM_ICON_MAP[key]
    || PLATFORM_ICON_MAP[raw.replace('半托', '').trim()]
    || null;
}

function pickTemplateCreatorFields(template) {
  return {
    ownerName:
      template?.ownerName
      || template?.owner_name
      || template?.creatorName
      || template?.creator_name
      || template?.createdByName
      || template?.created_by_name
      || template?.operatorName
      || template?.operator_name
      || template?.operator
      || template?.createdBy
      || template?.created_by
      || template?.creator
      || '',
    ownerId:
      template?.ownerId
      ?? template?.owner_id
      ?? template?.userId
      ?? template?.user_id
      ?? template?.createdByUserId
      ?? template?.ownerUserId
      ?? null,
  };
}

function resolveCreatorLabel(template, creatorOptions = []) {
  const { ownerId, ownerName } = pickTemplateCreatorFields(template);
  if (ownerId != null && ownerId !== '') {
    const hit = creatorOptions.find((o) => String(o.value) === String(ownerId));
    if (hit?.label) return hit.label;
  }
  const name = String(ownerName || '').trim();
  return name || '—';
}

const EMPTY_FILTERS = {
  name: '',
  creatorUserId: '',
  platform: '',
};

function filtersKey(filters, group) {
  return [
    filters.name || '',
    filters.creatorUserId || '',
    filters.platform || '',
    group || '',
  ].join('|');
}

export default function ListingTemplatePickerModal({
  isOpen,
  onCancel,
  onConfirm,
  groups: groupsProp = [],
  selectedGroup = '',
  onGroupChange,
  selectedTemplateIds = [],
  onToggleTemplate,
  selectedPlatform = '',
  onPlatformChange,
}) {
  void selectedPlatform;

  const { t } = useTranslation('workbench');
  const workspaceMode = useIpeWorkspaceMode();
  const pageSizeSelectOptions = useMemo(() => buildPageSizeSelectOptions(t), [t]);
  const showCreatorFilter = workspaceMode === 'team';
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LISTING_EXPORT_DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [groupNames, setGroupNames] = useState([]);
  const [creatorOptions, setCreatorOptions] = useState(
    () => [{ value: '', label: t('listingTemplatePicker.groupAll') }],
  );
  const [platformOptions, setPlatformOptions] = useState(
    () => [{ value: '', label: t('listingTemplatePicker.allPlatforms') }],
  );

  const accessRef = useRef(null);
  const pageCacheRef = useRef({});
  const loadSeqRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (showCreatorFilter) return;
    setFilterDraft((prev) => (prev.creatorUserId ? { ...prev, creatorUserId: '' } : prev));
    setAppliedFilters((prev) => (prev.creatorUserId ? { ...prev, creatorUserId: '' } : prev));
  }, [showCreatorFilter]);

  const effectiveGroup = selectedGroup || '';

  const groupOptions = useMemo(
    () => Array.from(new Set([...(groupsProp || []), ...groupNames].filter(Boolean))),
    [groupsProp, groupNames],
  );

  const resetPaginationState = useCallback(() => {
    pageCacheRef.current = {};
    setPage(1);
    setHasMore(false);
    setTemplates([]);
  }, []);

  const loadPage = useCallback(async (targetPage, size, filters, group) => {
    const key = `${filtersKey(filters, group)}::${size}::${targetPage}`;
    const cached = pageCacheRef.current[key];
    if (cached) {
      setTemplates(cached.templates);
      setHasMore(cached.hasMore);
      setPage(targetPage);
      return;
    }

    const result = await loadTableListingTemplatesCursorPage({
      pageSize: size,
      cursor: targetPage > 1 ? { serverPage: targetPage } : null,
      filters: { ...filters, group },
      access: accessRef.current,
    });
    if (result.access) accessRef.current = result.access;

    pageCacheRef.current[key] = {
      templates: result.templates,
      hasMore: result.hasMore,
    };

    setTemplates(result.templates);
    setHasMore(result.hasMore);
    setPage(targetPage);

    if (result.discoveredGroups?.length) {
      setGroupNames((prev) => Array.from(new Set([...prev, ...result.discoveredGroups])));
    }

    const platforms = Array.from(
      new Set(result.templates.map((t) => t?.platform).filter(Boolean)),
    );
    if (platforms.length) {
      setPlatformOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));
        platforms.forEach((p) => {
          if (!map.has(p)) map.set(p, { value: p, label: p });
        });
        return Array.from(map.values());
      });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFilterDraft(EMPTY_FILTERS);
      setAppliedFilters(EMPTY_FILTERS);
      resetPaginationState();
      setPageSize(LISTING_EXPORT_DEFAULT_PAGE_SIZE);
      loadSeqRef.current += 1;
      return undefined;
    }

    let cancelled = false;
    const seq = ++loadSeqRef.current;

    (async () => {
      setIsLoading(true);
      try {
        // 按当前个人/团队工作区解析 ACL 与数据源
        const [access, names] = await Promise.all([
          resolveTableListingTemplatesAccess(),
          loadTableListingTemplateGroupNames(),
        ]);
        if (cancelled || loadSeqRef.current !== seq) return;
        accessRef.current = access;
        setCreatorOptions(access.creatorOptions || [{ value: '', label: t('listingTemplatePicker.groupAll') }]);
        setGroupNames(names);
        resetPaginationState();
        await loadPage(1, LISTING_EXPORT_DEFAULT_PAGE_SIZE, EMPTY_FILTERS, '');
      } catch (error) {
        console.error('[按模板导出] 加载模板失败:', error);
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled && loadSeqRef.current === seq) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceMode, loadPage, resetPaginationState, t]);

  const totalPages = Math.max(1, page + (hasMore ? 1 : 0));
  const totalItems = hasMore
    ? page * pageSize + 1
    : Math.max((page - 1) * pageSize + templates.length, templates.length);

  const isChecked = (id) => selectedTemplateIds.includes(id);
  const handleRowToggle = (tpl) => {
    onToggleTemplate?.(tpl);
  };

  const allVisibleSelected = templates.length > 0 && templates.every((t) => isChecked(t.id));
  const someVisibleSelected = templates.some((t) => isChecked(t.id));

  const handleToggleAll = () => {
    if (!onToggleTemplate) return;
    if (allVisibleSelected) {
      templates.forEach((tpl) => {
        if (isChecked(tpl.id)) onToggleTemplate(tpl);
      });
    } else {
      templates.forEach((tpl) => {
        if (!isChecked(tpl.id)) onToggleTemplate(tpl);
      });
    }
  };

  const handleGoCreate = () => {
    onCancel?.();
    navigate('/ipe', { state: { activeNav: 'tableTemplates' } });
  };

  const handleDraftChange = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const runQuery = async (nextFilters, nextGroup, nextPageSize = pageSize) => {
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    try {
      pageCacheRef.current = {};
      await loadPage(1, nextPageSize, nextFilters, nextGroup);
    } catch (error) {
      console.error('[按模板导出] 查询失败:', error);
      setTemplates([]);
    } finally {
      if (loadSeqRef.current === seq) setIsLoading(false);
    }
  };

  const handleQuery = () => {
    const nextFilters = showCreatorFilter
      ? { ...filterDraft }
      : { ...filterDraft, creatorUserId: '' };
    setFilterDraft(nextFilters);
    setAppliedFilters(nextFilters);
    onPlatformChange?.(nextFilters.platform || '');
    runQuery(nextFilters, effectiveGroup, pageSize);
  };

  const handleReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    onPlatformChange?.('');
    onGroupChange?.('');
    runQuery(EMPTY_FILTERS, '', pageSize);
  };

  const handleGroupSelect = (group) => {
    onGroupChange?.(group);
    runQuery(appliedFilters, group, pageSize);
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage === page || isLoading) return;
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    try {
      await loadPage(nextPage, pageSize, appliedFilters, effectiveGroup);
    } catch (error) {
      console.error('[按模板导出] 翻页失败:', error);
    } finally {
      if (loadSeqRef.current === seq) setIsLoading(false);
    }
  };

  const handlePageSizeChange = (nextSize) => {
    const size = Number(nextSize) || LISTING_EXPORT_DEFAULT_PAGE_SIZE;
    setPageSize(size);
    runQuery(appliedFilters, effectiveGroup, size);
  };

  if (!isOpen) return null;

  return (
    <div className="funding-modal funding-modal--top">
      <div className="funding-modal__backdrop" role="presentation" />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--listing-template-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="listing-template-picker-title"
      >
        <header className="funding-modal__header">
          <h3 id="listing-template-picker-title">{t('listingTemplatePicker.title')}</h3>
        </header>
        <div className="funding-modal__body">
          <div className="funding-listing-tpl-picker__filters" role="search">
            <div className="funding-listing-tpl-picker__create-hint">
              <span>{t('listingTemplatePicker.noTemplates')}</span>
              <button
                type="button"
                className="funding-listing-tpl-picker__create-link"
                onClick={handleGoCreate}
              >
                {t('listingTemplatePicker.goCreate')}
              </button>
            </div>
            <div className="funding-listing-tpl-picker__filter-fields">
              <div className="funding-listing-tpl-picker__search">
                <input
                  id="listing-tpl-name-query"
                  type="text"
                  className="funding-listing-tpl-picker__search-input"
                  placeholder={t('listingTemplatePicker.searchPh')}
                  value={filterDraft.name}
                  onChange={(e) => handleDraftChange('name', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleQuery();
                    }
                  }}
                  autoComplete="off"
                />
                {filterDraft.name.trim() ? (
                  <button
                    type="button"
                    className="funding-listing-tpl-picker__search-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDraftChange('name', '');
                    }}
                    aria-label={t('listingTemplatePicker.clearSearchAria')}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              {showCreatorFilter ? (
                <div className="funding-listing-tpl-picker__field">
                  <IpeToolbarSelect
                    id="listing-tpl-creator"
                    className="funding-listing-tpl-picker__select"
                    value={filterDraft.creatorUserId}
                    onChange={(v) => handleDraftChange('creatorUserId', v)}
                    options={creatorOptions}
                    width={140}
                    ariaLabel={t('listingTemplatePicker.creatorAria')}
                    clearable={false}
                  />
                </div>
              ) : null}
              <div className="funding-listing-tpl-picker__field">
                <IpeToolbarSelect
                  id="listing-tpl-platform"
                  className="funding-listing-tpl-picker__select"
                  value={filterDraft.platform}
                  onChange={(v) => handleDraftChange('platform', v)}
                  options={platformOptions}
                  width={140}
                  ariaLabel={t('listingTemplatePicker.platformAria')}
                  clearable={false}
                />
              </div>
              <button
                type="button"
                className="funding-listing-tpl-picker__btn funding-listing-tpl-picker__btn--reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                {t('listingTemplatePicker.reset')}
              </button>
              <button
                type="button"
                className="funding-listing-tpl-picker__btn funding-listing-tpl-picker__btn--query"
                onClick={handleQuery}
                disabled={isLoading}
              >
                {isLoading ? t('listingTemplatePicker.querying') : t('listingTemplatePicker.query')}
              </button>
            </div>
          </div>

          <div className="funding-listing-tpl-picker__groups" aria-label={t('listingTemplatePicker.groupsAria')}>
            <button
              type="button"
              className={`funding-listing-tpl-picker__group-chip${!effectiveGroup ? ' is-active' : ''}`}
              onClick={() => handleGroupSelect('')}
            >
              {t('listingTemplatePicker.groupAll')}
            </button>
            <button
              type="button"
              className={`funding-listing-tpl-picker__group-chip${effectiveGroup === LISTING_EXPORT_GROUP_UNGROUPED ? ' is-active' : ''}`}
              onClick={() => handleGroupSelect(LISTING_EXPORT_GROUP_UNGROUPED)}
            >
              {t('listingTemplatePicker.groupUngrouped')}
            </button>
            {groupOptions.map((g) => (
              <button
                key={g}
                type="button"
                className={`funding-listing-tpl-picker__group-chip${effectiveGroup === g ? ' is-active' : ''}`}
                onClick={() => handleGroupSelect(g)}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="funding-listing-tpl-picker__table-wrap" role="region" aria-live="polite">
            {isLoading && templates.length === 0 ? (
              <div className="funding-listing-tpl-picker__empty">
                <p>{t('listingTemplatePicker.loading')}</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="funding-listing-tpl-picker__empty">
                <p>{t('listingTemplatePicker.empty')}</p>
              </div>
            ) : (
              <table className="funding-listing-tpl-picker__table">
                <thead>
                  <tr>
                    <th scope="col" className="funding-listing-tpl-picker__col-check">
                      <input
                        type="checkbox"
                        className="funding-listing-tpl-picker__check"
                        checked={allVisibleSelected}
                        onChange={handleToggleAll}
                        ref={(el) => {
                          if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                        }}
                        aria-label={t('listingTemplatePicker.selectAllPage')}
                      />
                    </th>
                    <th scope="col" className="funding-listing-tpl-picker__col-index">{t('listingTemplatePicker.colIndex')}</th>
                    <th scope="col" className="funding-listing-tpl-picker__col-name">{t('listingTemplatePicker.colName')}</th>
                    <th scope="col" className="funding-listing-tpl-picker__col-platform">{t('listingTemplatePicker.colPlatform')}</th>
                    <th scope="col" className="funding-listing-tpl-picker__col-creator">{t('listingTemplatePicker.colCreator')}</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tpl, index) => {
                    const iconSrc = resolvePlatformIcon(tpl.platform);
                    const selected = isChecked(tpl.id);
                    const rowNo = (page - 1) * pageSize + index + 1;
                    return (
                      <tr
                        key={tpl.id}
                        className={selected ? 'is-selected' : ''}
                        onClick={() => handleRowToggle(tpl)}
                      >
                        <td className="funding-listing-tpl-picker__col-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="funding-listing-tpl-picker__check"
                            checked={selected}
                            onChange={() => handleRowToggle(tpl)}
                            aria-label={t('listingTemplatePicker.selectRow', { name: tpl.name || tpl.id })}
                          />
                        </td>
                        <td className="funding-listing-tpl-picker__col-index">{rowNo}</td>
                        <td className="funding-listing-tpl-picker__col-name">
                          <span className="funding-listing-tpl-picker__name">{tpl.name || '—'}</span>
                        </td>
                        <td className="funding-listing-tpl-picker__col-platform">
                          <div className="funding-listing-tpl-picker__platform">
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt=""
                                className="funding-listing-tpl-picker__platform-icon"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : null}
                            <span>{tpl.platform || '—'}</span>
                          </div>
                        </td>
                        <td className="funding-listing-tpl-picker__col-creator">
                          {resolveCreatorLabel(tpl, creatorOptions)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {templates.length > 0 || page > 1 || hasMore ? (
            <div className="funding-listing-tpl-picker__pagination">
              <WfTasksPagination
                variant="element"
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeSelectOptions}
                showTotal={false}
                ariaLabel={t('listingTemplatePicker.paginationAria')}
              />
            </div>
          ) : null}
        </div>
        <footer className="funding-modal__footer">
          <button type="button" className="funding-button funding-button--ghost" onClick={onCancel}>{t('commonActions.cancel')}</button>
          <button type="button" className="funding-button funding-button--accent" onClick={onConfirm}>{t('commonActions.confirm')}</button>
        </footer>
      </div>
    </div>
  );
}
